/**
 * 模型缓存与实例化（P0-3c 多对象渲染的基础设施）。
 *
 * 两条硬规则（都是拿实测教训换的）：
 *  1. **同一 URL 只加载一次**，之后每个对象用 SkeletonUtils.clone 拿独立副本——
 *     直接复用同一 scene 实例会让多个 SkinnedMesh 共享骨架，一个姿势全员串改；
 *     普通 Object3D.clone() 不处理蒙皮引用，必须用 SkeletonUtils 版本。
 *  2. **准备四步顺序固定**（骨名规范化→剔道具→自动拟合→捕获 rest），
 *     与第五轮定案一致；fit 在 rest 之前，rest 必须最后捕获。
 */
import type { Object3D } from 'three';
import { Box3, Vector3 } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';

import {
  captureRest,
  computeAutoFitScale,
  normalizeBoneNames,
  stripPropMeshes,
  type RestSnapshot,
} from './poseApply';

export interface PreparedCharacter {
  root: Object3D;
  rest: RestSnapshot;
  fitScale: number;
  removedProps: string[];
  /** root 在父容器空间的包围盒（第六轮实测：SkinnedMesh 射线检测不可靠，用它做拾取代理） */
  bounds: { center: [number, number, number]; size: [number, number, number] };
}

const cache = new Map<string, Promise<Object3D>>();

function loadOnce(url: string): Promise<Object3D> {
  let p = cache.get(url);
  if (p === undefined) {
    p = new Promise<Object3D>((resolve, reject) => {
      new GLTFLoader().load(
        url,
        (gltf) => resolve(gltf.scene),
        undefined,
        (err) => reject(err instanceof Error ? err : new Error(String(err))),
      );
    });
    // 失败不缓存：否则一次网络抖动会让该模型永久不可用
    p.catch(() => cache.delete(url));
    cache.set(url, p);
  }
  return p;
}

/** 取一个可独立上姿势的角色实例（每次调用返回新副本） */
export async function acquireCharacter(url: string): Promise<PreparedCharacter> {
  const template = await loadOnce(url);
  const root = cloneSkeleton(template);
  normalizeBoneNames(root);
  const removedProps = stripPropMeshes(root);
  const height = new Box3().setFromObject(root).getSize(new Vector3()).y;
  const fitScale = computeAutoFitScale(height);
  if (fitScale !== 1) {
    root.scale.setScalar(fitScale);
    const fitted = new Box3().setFromObject(root);
    root.position.y -= fitted.min.y;
  }
  const rest = captureRest(root);
  // 包围盒必须在挂进场景图**之前**算：此时 matrixWorld 就是 root 自己的局部变换，
  // 得到的盒正好落在内层 group（uniformScale）空间，拾取代理直接复用
  const b = new Box3().setFromObject(root);
  const c = b.getCenter(new Vector3());
  const sz = b.getSize(new Vector3());
  return {
    root,
    rest,
    fitScale,
    removedProps,
    bounds: {
      center: [c.x, c.y, c.z],
      size: [Math.max(sz.x, 0.05), Math.max(sz.y, 0.05), Math.max(sz.z, 0.05)],
    },
  };
}

/** 仅供测试：清空缓存 */
export function __clearModelCache(): void {
  cache.clear();
}
